import { Pin } from "@/icons";
import { cn } from "@/lib/cn";
import { formatAddressLines, type AddressParts } from "@/lib/format-address";
import { forwardRef, type HTMLAttributes } from "react";
import { Icon } from "./Icon";
import { Text } from "./Typography";

/**
 * Props of `AddressSummary`; `compact` is one truncated line, `full` the two canonical lines plus complement.
 * `size="lg"` (full only) sets the street at `lg` and the other lines at `base`, for a list of address cards.
 */
export type AddressSummaryProps = Omit<HTMLAttributes<HTMLDivElement>, "children"> & {
  address: AddressParts;
  variant?: "full" | "compact";
  size?: "md" | "lg";
  showIcon?: boolean;
};

/** The storefront's one address rendering (checkout, order, profile, header), built on `formatAddressLines`. */
export const AddressSummary = forwardRef<HTMLDivElement, AddressSummaryProps>(function AddressSummary(
  { address, variant = "full", size = "md", showIcon = false, className, ...rest },
  ref,
) {
  const icon = showIcon ? <Icon as={Pin} size={variant === "compact" ? "md" : "lg"} tone="primary" /> : null;

  if (variant === "compact") {
    return (
      <div {...rest} ref={ref} className={cn("flex min-w-0 items-center gap-2", className)}>
        {icon}
        <Text as="span" tone="muted" truncate className="min-w-0">
          {formatAddressLines(address, "compact")}
        </Text>
      </div>
    );
  }

  const { line1, line2, complement } = formatAddressLines(address);
  return (
    <div {...rest} ref={ref} className={cn("flex min-w-0 items-start gap-3", className)}>
      {icon}
      <div className="flex min-w-0 flex-col gap-0.5">
        <Text as="span" size={size === "lg" ? "lg" : undefined} weight="semibold" tone="default">
          {line1}
        </Text>
        {line2 ? (
          <Text as="span" size={size === "lg" ? "base" : "sm"} tone="muted">
            {line2}
          </Text>
        ) : null}
        {complement ? (
          <Text as="span" size={size === "lg" ? "base" : "sm"} tone="subtle">
            {complement}
          </Text>
        ) : null}
      </div>
    </div>
  );
});
